import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useUI } from '../contexts/UIProvider';
import * as api from '../services/apiService';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import ShareIcon from './icons/ShareIcon';
import { formatTime } from '../utils/formatTimestamp';
import type { FlyerConfig, FlyerFont, FlyerTheme, Lineup } from '../types';
import ChevronDownIcon from './icons/ChevronDownIcon';
import PhotoIcon from './icons/PhotoIcon';
import TrashIcon from './icons/TrashIcon';
import SettingsIcon from './icons/SettingsIcon';
import CloseIcon from './icons/CloseIcon';

type FlyerPage = 'graphic' | 'lineup';
type InteractionMode = 'none' | 'move' | 'resize';
type ActiveElement = { type: 'artist', id: string } | { type: 'header' } | null;

const themeColors: Record<FlyerTheme, { bg: string; surface: string; text: string; primary: string; muted: string; }> = {
    dark: { bg: '#121212', text: '#FFFFFF', primary: '#FF4D00', muted: '#A0A0A0', surface: '#1E1E1E' },
    light: { bg: '#F3F4F6', text: '#121212', primary: '#FF4D00', muted: '#6B7280', surface: '#FFFFFF' },
};

const fontMap: Record<FlyerFont, string> = {
    Modern: 'sans-serif', Classic: 'serif', Impact: 'Impact, sans-serif', 'Roboto Slab': '"Roboto Slab", serif', 'Playfair Display': '"Playfair Display", serif', Oswald: '"Oswald", sans-serif', Anton: '"Anton", sans-serif', Urbanist: '"Urbanist", sans-serif', 'Russo One': '"Russo One", sans-serif', 'Jersey 10': '"Jersey 10", sans-serif',
};

const colorOptions = ['#FFFFFF', '#000000', '#FF4D00', '#3B82F6', '#22C55E', '#FBBF24', '#EC4899'];

const loadImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
        console.warn(`Failed to load image: ${src}`);
        reject(new Error(`Failed to load image: ${src}`));
    };
    img.src = src;
});

const drawCommonHeader = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: Partial<FlyerConfig>, colors: any, activeElement: ActiveElement, isSaveVersion = false) => {
    const font = config.font || 'Oswald';
    const layout = config.headerLayout || { x: 50, y: 15, scale: 100 };
    const centerX = canvas.width * (layout.x / 100);
    const centerY = canvas.height * (layout.y / 100);
    const scale = layout.scale / 100;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    let currentY = centerY;

    if (config.showMainTitle) {
        ctx.fillStyle = config.titleColor || colors.text;
        ctx.font = `bold ${canvas.width * 0.08 * scale}px ${fontMap[font]}`;
        const title = config.flyerTitle || 'UNTITLED EVENT';
        ctx.fillText(title.toUpperCase(), centerX, currentY);
        currentY += (canvas.width * 0.1 * scale);
    }

    if (config.showMainDate || config.showMainLocation) {
        // Synchronized color for the whole header block
        ctx.fillStyle = config.titleColor || colors.primary;
        ctx.font = `bold ${canvas.width * 0.04 * scale}px ${fontMap[font]}`;
        let info = "";
        if (config.showMainDate) info += config.flyerDate || 'DATE TBD';
        if (config.showMainDate && config.showMainLocation) info += " @ ";
        if (config.showMainLocation) info += config.flyerLocation || 'LOCATION TBD';
        ctx.fillText(info.toUpperCase(), centerX, currentY);
        currentY += (canvas.width * 0.06 * scale);
    }

    if (!isSaveVersion && activeElement?.type === 'header') {
        const hWidth = canvas.width * 0.7 * scale;
        const hHeight = Math.max(80, currentY - centerY + 20);
        ctx.strokeStyle = '#FF4D00';
        ctx.lineWidth = 4;
        ctx.strokeRect(centerX - hWidth / 2, centerY - 10, hWidth, hHeight);

        const handleX = centerX + hWidth / 2;
        const handleY = centerY + hHeight - 10;
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(handleX, handleY, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }
};

const drawWatermark = async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, colors: any) => {
    const FOOTER_HEIGHT = canvas.height * 0.08;
    const footerY = canvas.height - FOOTER_HEIGHT;
    const WATERMARK_PADDING = 20;
    const WATERMARK_HEIGHT = FOOTER_HEIGHT * 0.6;
    const WATERMARK_LOGO_SIZE = WATERMARK_HEIGHT * 0.8;
    const logoSvg = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="${colors.primary}" width="${WATERMARK_LOGO_SIZE}" height="${WATERMARK_LOGO_SIZE}"><path d="M4 2h16a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2z" stroke-width="1.5"/><path d="M2 8h20" stroke-width="1.5"/><circle cx="7" cy="5" r="1" fill="${colors.primary}" stroke="none" /><circle cx="12" cy="5" r="1" fill="${colors.primary}" stroke="none" /><circle cx="17" cy="5" r="1" fill="${colors.primary}" stroke="none" /><path d="M6 15a6 6 0 0112 0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>`;
    const logoImage = await loadImage(`data:image/svg+xml;base64,${btoa(logoSvg)}`);
    ctx.font = `bold ${WATERMARK_HEIGHT * 0.5}px Urbanist`;
    const text = 'beYourAMP';
    const textMetrics = ctx.measureText(text);
    const WATERMARK_WIDTH = WATERMARK_LOGO_SIZE + textMetrics.width + WATERMARK_PADDING * 1;
    const watermarkX = canvas.width - WATERMARK_WIDTH - WATERMARK_PADDING;
    const watermarkY = footerY + (FOOTER_HEIGHT - WATERMARK_HEIGHT) / 2;
    ctx.fillStyle = 'black'; ctx.fillRect(watermarkX, watermarkY, WATERMARK_WIDTH, WATERMARK_HEIGHT);
    ctx.drawImage(logoImage, watermarkX + WATERMARK_PADDING / 2, watermarkY + (WATERMARK_HEIGHT - WATERMARK_LOGO_SIZE) / 2);
    ctx.fillStyle = colors.primary; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(text, watermarkX + WATERMARK_LOGO_SIZE + WATERMARK_PADDING / 2, watermarkY + WATERMARK_HEIGHT / 2);
};

const FlyerPreviewPage: React.FC = () => {
    const { viewingOpportunity, viewingLineup, handleNavigation } = useNavigation();
    const { showToast } = useUI();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isRendering, setIsRendering] = useState(false);
    const [currentPage, setCurrentPage] = useState<FlyerPage>('graphic');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [flyerConfig, setFlyerConfig] = useState<Partial<FlyerConfig>>({
        flyerTitle: '', flyerLocation: '', flyerDate: '', artistImageSize: 100, lineupFontSize: 24, theme: 'dark' as FlyerTheme, font: 'Oswald' as FlyerFont, titleColor: '#FFFFFF',
        showMainTitle: true, showMainDate: true, showMainLocation: true, showArtistImages: false, showPerformanceHours: true, showDayBadge: true, showDayBadgeBg: true, showArtistsOnGraphic: true,
        headerLayout: { x: 50, y: 15, scale: 100 },
        artistLayouts: {}
    });
    const [zoom, setZoom] = useState(25);
    const [graphicPageBgImg, setGraphicPageBgImg] = useState<HTMLImageElement | null>(null);
    const [lineupPageBgImg, setLineupPageBgImg] = useState<HTMLImageElement | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSharing, setIsSharing] = useState(false);

    // Interaction State
    const [activeElement, setActiveElement] = useState<ActiveElement>(null);
    const [interactionMode, setInteractionMode] = useState<InteractionMode>('none');
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [initialScale, setInitialScale] = useState(100);
    const [initialDist, setInitialDist] = useState(0);

    const updateConfig = (updater: (prev: Partial<FlyerConfig>) => Partial<FlyerConfig>) => setFlyerConfig(updater);

    useEffect(() => {
        if (viewingOpportunity) {
            updateConfig(c => ({
                ...c,
                flyerTitle: viewingOpportunity.title,
                flyerLocation: viewingOpportunity.location,
                flyerDate: viewingOpportunity.startDate ? new Date(`${viewingOpportunity.startDate}T00:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '',
            }));
        }
    }, [viewingOpportunity]);

    const drawGraphicPage = useCallback(async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, overrideConfig?: Partial<FlyerConfig>, isSaveVersion = false) => {
        if (!viewingLineup || !flyerConfig.theme) return;
        const config = overrideConfig ? { ...flyerConfig, ...overrideConfig } : flyerConfig;
        const colors = themeColors[config.theme as FlyerTheme];
        const font = config.font || 'Oswald';

        ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        if (isSaveVersion) {
            ctx.save();
            const scale = 0.92;
            ctx.translate(canvas.width * (1 - scale) / 2, canvas.height * (1 - scale) / 2);
            ctx.scale(scale, scale);
        }

        if (graphicPageBgImg) {
            const ratio = Math.max(canvas.width / graphicPageBgImg.width, canvas.height / graphicPageBgImg.height);
            ctx.drawImage(graphicPageBgImg, (canvas.width - graphicPageBgImg.width * ratio) / 2, (canvas.height - graphicPageBgImg.height * ratio) / 2, graphicPageBgImg.width * ratio, graphicPageBgImg.height * ratio);
            ctx.fillStyle = 'rgba(0,0,0,0.3)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        drawCommonHeader(ctx, canvas, config, colors, activeElement, isSaveVersion);

        if (config.showArtistsOnGraphic) {
            const lineup = viewingLineup as Lineup;
            const slots = lineup.days?.flatMap(d => d.slots || []) || [];
            
            for (const slot of slots) {
                const layout = config.artistLayouts?.[slot.id] || { x: 50, y: 55, scale: 100 };
                const baseSize = canvas.width * 0.25;
                const size = baseSize * (layout.scale / 100);
                const centerX = (canvas.width * (layout.x / 100));
                const centerY = (canvas.height * (layout.y / 100));
                const x = centerX - (size / 2);
                const y = centerY - (size / 2);

                const avatarUrl = slot.artistImageUrl || slot.artist.avatarUrl;
                try {
                    const artistImg = await loadImage(avatarUrl);
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
                    ctx.clip();
                    ctx.drawImage(artistImg, x, y, size, size);
                    ctx.restore();
                    
                    if (!isSaveVersion && activeElement?.type === 'artist' && activeElement.id === slot.id) {
                        ctx.strokeStyle = '#FF4D00';
                        ctx.lineWidth = 4;
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, size / 2 + 5, 0, Math.PI * 2);
                        ctx.stroke();

                        const handleX = centerX + (size / 2) * Math.cos(Math.PI / 4);
                        const handleY = centerY + (size / 2) * Math.sin(Math.PI / 4);
                        ctx.fillStyle = '#FFFFFF';
                        ctx.beginPath();
                        ctx.arc(handleX, handleY, 15, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.strokeStyle = '#FF4D00';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                    }
                    
                    ctx.font = `bold ${size * 0.15}px ${fontMap[font]}`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    ctx.fillStyle = 'black';
                    ctx.fillText(slot.artist.name.toUpperCase(), centerX + 2, y + size + 12);
                    ctx.fillStyle = colors.text;
                    ctx.fillText(slot.artist.name.toUpperCase(), centerX, y + size + 10);
                } catch (e) {
                    ctx.fillStyle = colors.surface;
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        }

        if (isSaveVersion) ctx.restore();
        await drawWatermark(ctx, canvas, colors);
    }, [viewingLineup, flyerConfig, graphicPageBgImg, activeElement]);

    const drawLineupPage = useCallback(async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, overrideConfig?: Partial<FlyerConfig>, isSaveVersion = false) => {
        if (!viewingLineup || !flyerConfig.theme) return;
        const config = overrideConfig ? { ...flyerConfig, ...overrideConfig } : flyerConfig;
        const font = config.font || 'Oswald';
        const colors = themeColors[config.theme as FlyerTheme];
        const PADDING = canvas.width * 0.1;

        ctx.fillStyle = colors.bg; ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (isSaveVersion) {
            ctx.save();
            const scale = 0.92;
            ctx.translate(canvas.width * (1 - scale) / 2, canvas.height * (1 - scale) / 2);
            ctx.scale(scale, scale);
        }

        if (lineupPageBgImg) {
            const ratio = Math.max(canvas.width / lineupPageBgImg.width, canvas.height / lineupPageBgImg.height);
            ctx.drawImage(lineupPageBgImg, (canvas.width - lineupPageBgImg.width * ratio) / 2, (canvas.height - lineupPageBgImg.height * ratio) / 2, lineupPageBgImg.width * ratio, lineupPageBgImg.height * ratio);
        }

        const boxWidth = canvas.width * 0.85;
        const boxX = (canvas.width - boxWidth) / 2;
        const boxY = PADDING * 4;
        const boxHeight = canvas.height - boxY - (PADDING * 2.5);
        
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.beginPath();
        const r = 20;
        ctx.moveTo(boxX + r, boxY);
        ctx.arcTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + boxHeight, r);
        ctx.arcTo(boxX + boxWidth, boxY + boxHeight, boxX, boxY + boxHeight, r);
        ctx.arcTo(boxX, boxY + boxHeight, boxX, boxY, r);
        ctx.arcTo(boxX, boxY, boxX + boxWidth, boxY, r);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.lineWidth = 1;
        ctx.stroke();

        drawCommonHeader(ctx, canvas, config, colors, activeElement, isSaveVersion);

        let currentY = boxY + PADDING * 0.5;
        const currentLineup = viewingLineup as Lineup;

        for (const day of currentLineup.days || []) {
            const dayNum = day.date ? new Date(day.date).getDate() : '?';
            const badgeRadius = canvas.width * 0.045;
            const contentX = boxX + PADDING * 0.6;
            const badgeX = contentX + badgeRadius;
            
            if (config.showDayBadge) {
                if (config.showDayBadgeBg) {
                    ctx.beginPath();
                    ctx.arc(badgeX, currentY + badgeRadius, badgeRadius, 0, Math.PI * 2);
                    ctx.fillStyle = colors.primary;
                    ctx.fill();
                    ctx.fillStyle = '#FFFFFF';
                } else {
                    ctx.fillStyle = colors.primary;
                }
                
                ctx.font = `bold ${canvas.width * 0.045}px ${fontMap[font]}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(dayNum), badgeX, currentY + badgeRadius);
            }

            currentY += badgeRadius * 2.5;

            for (const slot of day.slots || []) {
                const rowHeight = canvas.width * 0.08;

                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = colors.text;
                ctx.font = `bold ${canvas.width * 0.042}px ${fontMap[font]}`;
                
                const nameX = contentX;
                ctx.fillText(slot.artist.name.toUpperCase(), nameX, currentY + rowHeight/2);

                if (config.showPerformanceHours) {
                    ctx.textAlign = 'right';
                    ctx.fillStyle = colors.primary;
                    ctx.font = `bold ${canvas.width * 0.035}px ${fontMap[font]}`;
                    const timeStr = formatTime(slot.startTime);
                    ctx.fillText(timeStr, boxX + boxWidth - PADDING * 0.6, currentY + rowHeight/2);
                }

                currentY += rowHeight + 8;
            }
            currentY += PADDING * 0.6;
        }

        if (isSaveVersion) ctx.restore();
        await drawWatermark(ctx, canvas, colors);
    }, [viewingLineup, flyerConfig, lineupPageBgImg, activeElement]);

    const drawCurrentPage = useCallback(async () => {
        const canvas = canvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d'); if (!ctx) return;
        setIsRendering(true);
        canvas.width = 1080; canvas.height = 1920;
        try {
            if (currentPage === 'graphic') await drawGraphicPage(ctx, canvas);
            else await drawLineupPage(ctx, canvas);
        } finally { setIsRendering(false); }
    }, [currentPage, drawGraphicPage, drawLineupPage]);

    useEffect(() => { drawCurrentPage(); }, [drawCurrentPage, viewingLineup, currentPage, flyerConfig]);

    // INTERACTION LOGIC
    const getCanvasCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const { x, y } = getCanvasCoords(e);
        const slots = (viewingLineup as Lineup).days?.flatMap(d => d.slots || []) || [];
        
        let foundElement: ActiveElement = null;
        let mode: InteractionMode = 'none';

        const hLayout = flyerConfig.headerLayout || { x: 50, y: 15, scale: 100 };
        const hCenterX = (1080 * (hLayout.x / 100));
        const hCenterY = (1920 * (hLayout.y / 100));
        const hScale = hLayout.scale / 100;
        const hWidth = 1080 * 0.7 * hScale;
        const hHeight = 300 * hScale;

        const hHandleX = hCenterX + hWidth / 2;
        const hHandleY = hCenterY + hHeight - 10;
        if (Math.sqrt(Math.pow(x - hHandleX, 2) + Math.pow(y - hHandleY, 2)) < 45) {
            foundElement = { type: 'header' };
            mode = 'resize';
            setInitialScale(hLayout.scale);
            setInitialDist(Math.sqrt(Math.pow(x - hCenterX, 2) + Math.pow(y - hCenterY, 2)));
        } else if (x > hCenterX - hWidth/2 && x < hCenterX + hWidth/2 && y > hCenterY - 10 && y < hCenterY + hHeight) {
            foundElement = { type: 'header' };
            mode = 'move';
            setDragOffset({ x: x - hCenterX, y: y - hCenterY });
        }

        if (!foundElement && flyerConfig.showArtistsOnGraphic) {
            for (const slot of [...slots].reverse()) {
                const layout = flyerConfig.artistLayouts?.[slot.id] || { x: 50, y: 55, scale: 100 };
                const baseSize = 1080 * 0.25;
                const size = baseSize * (layout.scale / 100);
                const centerX = (1080 * (layout.x / 100));
                const centerY = (1920 * (layout.y / 100));
                
                const handleX = centerX + (size / 2) * Math.cos(Math.PI / 4);
                const handleY = centerY + (size / 2) * Math.sin(Math.PI / 4);
                const distToHandle = Math.sqrt(Math.pow(x - handleX, 2) + Math.pow(y - handleY, 2));

                if (distToHandle < 30) {
                    foundElement = { type: 'artist', id: slot.id };
                    mode = 'resize';
                    setInitialScale(layout.scale);
                    setInitialDist(Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)));
                    break;
                }

                const distToCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                if (distToCenter < size / 2) {
                    foundElement = { type: 'artist', id: slot.id };
                    mode = 'move';
                    setDragOffset({ x: x - centerX, y: y - centerY });
                    break;
                }
            }
        }

        setActiveElement(foundElement);
        setInteractionMode(mode);
        if (foundElement) {
            (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!activeElement || interactionMode === 'none') return;
        const { x, y } = getCanvasCoords(e);

        if (activeElement.type === 'header') {
            const layout = flyerConfig.headerLayout || { x: 50, y: 15, scale: 100 };
            if (interactionMode === 'move') {
                updateConfig(prev => ({ ...prev, headerLayout: { ...layout, x: ((x - dragOffset.x) / 1080) * 100, y: ((y - dragOffset.y) / 1920) * 100 } }));
            } else if (interactionMode === 'resize') {
                const centerX = (1080 * (layout.x / 100));
                const centerY = (1920 * (layout.y / 100));
                const currentDist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                const ratio = currentDist / initialDist;
                updateConfig(prev => ({ ...prev, headerLayout: { ...layout, scale: Math.max(20, Math.min(300, initialScale * ratio)) } }));
            }
        } else if (activeElement.type === 'artist') {
            const artistId = activeElement.id;
            const layout = flyerConfig.artistLayouts?.[artistId] || { x: 50, y: 55, scale: 100 };
            if (interactionMode === 'move') {
                updateArtistLayout(artistId, 'x', ((x - dragOffset.x) / 1080) * 100);
                updateArtistLayout(artistId, 'y', ((y - dragOffset.y) / 1920) * 100);
            } else if (interactionMode === 'resize') {
                const centerX = (1080 * (layout.x / 100));
                const centerY = (1920 * (layout.y / 100));
                const currentDist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                const ratio = currentDist / initialDist;
                updateArtistLayout(artistId, 'scale', Math.round(Math.max(20, Math.min(300, initialScale * ratio))));
            }
        }
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        setInteractionMode('none');
        (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
    };

    const handleSaveFlyer = async () => {
        if (!viewingOpportunity || !viewingLineup) return;
        setIsSaving(true);
        try {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = 480; 
            tempCanvas.height = 853; 
            const tempCtx = tempCanvas.getContext('2d')!;
            await drawGraphicPage(tempCtx, tempCanvas, undefined, true);
            const graphicData = tempCanvas.toDataURL('image/jpeg', 0.6);
            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            await drawLineupPage(tempCtx, tempCanvas, undefined, true);
            const lineupData = tempCanvas.toDataURL('image/jpeg', 0.6);
            const fullConfig: FlyerConfig = {
                ...(flyerConfig as FlyerConfig),
                id: Date.now().toString(),
                graphicBackground: graphicData,
                lineupBackground: lineupData,
            };
            await api.saveFlyer({
                opportunityId: viewingOpportunity.id,
                config: fullConfig,
                lineup: viewingLineup as Lineup
            });
            showToast('Optimized flyer saved!');
        } catch (error: any) { 
            console.error("Save Error:", error);
            showToast(`Save failed.`); 
        } finally { setIsSaving(false); }
    };

    const handleShare = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        setIsSharing(true);
        try {
            const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/png'));
            if (blob && navigator.share) {
                const file = new File([blob], 'flyer.png', { type: 'image/png' });
                await navigator.share({ files: [file], title: 'beYourAMP Flyer', text: `Flyer for ${viewingOpportunity?.title}` });
            } else { showToast("Sharing not supported."); }
        } catch (err) { console.error(err); } finally { setIsSharing(false); }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'graphic' | 'lineup') => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                if (event.target?.result) {
                    const img = await loadImage(event.target.result as string);
                    if (target === 'graphic') setGraphicPageBgImg(img);
                    else setLineupPageBgImg(img);
                }
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const updateArtistLayout = (slotId: string, field: 'x' | 'y' | 'scale', value: number) => {
        updateConfig(prev => ({
            ...prev,
            artistLayouts: {
                ...(prev.artistLayouts || {}),
                [slotId]: {
                    ...(prev.artistLayouts?.[slotId] || { x: 50, y: 55, scale: 100 }),
                    [field]: value
                }
            }
        }));
    };

    if (!viewingOpportunity || !viewingLineup) return null;

    const activeArtistId = activeElement?.type === 'artist' ? activeElement.id : null;

    const SidebarContent = (
        <div className="flex flex-col h-full space-y-4">
             {currentPage === 'graphic' && (
                <div className="bg-brand-orange/10 p-3 rounded-lg border border-brand-orange/20 mb-2">
                     <p className="text-[10px] font-black text-brand-orange uppercase tracking-widest mb-1">Interactive Stage</p>
                     <p className="text-[10px] text-gray-300 font-medium">Drag artists or the header directly on the canvas. Use the white corner handle to resize.</p>
                </div>
            )}
            
            <CollapsibleControlGroup label="Main Visibility" defaultOpen={true}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-[9px] font-bold text-brand-muted mb-1 uppercase tracking-widest">Main Title</label>
                        <input type="text" value={flyerConfig.flyerTitle} onChange={e => updateConfig(c => ({ ...c, flyerTitle: e.target.value }))} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-sm text-white font-bold outline-none focus:border-brand-orange" />
                    </div>
                    <div className="space-y-2 pt-2 border-t border-gray-700">
                        <CheckboxToggle label="Show Title" checked={flyerConfig.showMainTitle!} onChange={val => updateConfig(c => ({...c, showMainTitle: val}))} />
                        <CheckboxToggle label="Show Date" checked={flyerConfig.showMainDate!} onChange={val => updateConfig(c => ({...c, showMainDate: val}))} />
                        <CheckboxToggle label="Show Location" checked={flyerConfig.showMainLocation!} onChange={val => updateConfig(c => ({...c, showMainLocation: val}))} />
                    </div>
                </div>
            </CollapsibleControlGroup>

            <CollapsibleControlGroup label="Lineup Style" defaultOpen={currentPage === 'lineup'}>
                 <div className="space-y-3">
                    <CheckboxToggle label="Show Days" checked={flyerConfig.showDayBadge!} onChange={val => updateConfig(c => ({...c, showDayBadge: val}))} />
                    {flyerConfig.showDayBadge && (
                        <CheckboxToggle label="Badge Background" checked={flyerConfig.showDayBadgeBg!} onChange={val => updateConfig(c => ({...c, showDayBadgeBg: val}))} />
                    )}
                    <CheckboxToggle label="Set Times" checked={flyerConfig.showPerformanceHours!} onChange={val => updateConfig(c => ({...c, showPerformanceHours: val}))} />
                 </div>
            </CollapsibleControlGroup>

            <CollapsibleControlGroup label="Visual Style">
                <div className="space-y-4">
                    <Select label="Font" value={flyerConfig.font || 'Oswald'} onChange={e => updateConfig(c => ({ ...c, font: e.target.value as FlyerFont }))} options={['Modern', 'Classic', 'Oswald', 'Playfair Display', 'Roboto Slab', 'Impact', 'Anton', 'Urbanist', 'Russo One', 'Jersey 10']} />
                    <div>
                        <label className="block text-[9px] font-bold text-brand-muted mb-2 uppercase tracking-widest">Header Color</label>
                        <div className="flex flex-wrap gap-2">
                            {colorOptions.map(color => (
                                <button key={color} onClick={() => updateConfig(c => ({ ...c, titleColor: color }))} className={`w-6 h-6 rounded-full border-2 ${flyerConfig.titleColor === color ? 'border-brand-orange scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />
                            ))}
                        </div>
                    </div>
                </div>
            </CollapsibleControlGroup>

            {currentPage === 'graphic' && (
                <CollapsibleControlGroup label="Artists Layout" defaultOpen={true}>
                    <div className="space-y-4">
                        <CheckboxToggle label="Display Artists" checked={flyerConfig.showArtistsOnGraphic!} onChange={val => updateConfig(c => ({...c, showArtistsOnGraphic: val}))} />
                        {flyerConfig.showArtistsOnGraphic && (viewingLineup as Lineup).days?.flatMap(d => d.slots || []).map(slot => (
                            <div key={slot.id} className={`p-3 rounded-xl border transition-all space-y-3 ${activeArtistId === slot.id ? 'bg-brand-orange/10 border-brand-orange' : 'bg-gray-800/40 border-gray-700'}`}>
                                <div className="flex justify-between items-center">
                                    <p className={`text-[10px] font-black uppercase tracking-widest truncate ${activeArtistId === slot.id ? 'text-brand-orange' : 'text-gray-400'}`}>{slot.artist.name}</p>
                                    {activeArtistId === slot.id && <div className="h-2 w-2 rounded-full bg-brand-orange animate-pulse"></div>}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                     <input type="range" min="0" max="100" value={flyerConfig.artistLayouts?.[slot.id]?.x ?? 50} onChange={e => updateArtistLayout(slot.id, 'x', parseInt(e.target.value))} className="w-full accent-brand-orange" />
                                     <input type="range" min="20" max="250" value={flyerConfig.artistLayouts?.[slot.id]?.scale ?? 100} onChange={e => updateArtistLayout(slot.id, 'scale', parseInt(e.target.value))} className="w-full accent-brand-orange" />
                                </div>
                            </div>
                        ))}
                    </div>
                </CollapsibleControlGroup>
            )}

            <CollapsibleControlGroup label="AI Designer">
                <div className="bg-brand-dark/40 p-3 rounded-xl border border-gray-700 space-y-3">
                    <p className="text-[10px] text-gray-400 italic">
                        AI Image Generation will be available in the next Pro+ update.
                    </p>
                </div>
            </CollapsibleControlGroup>

            <CollapsibleControlGroup label="Backgrounds">
                <div className="space-y-2">
                    <UploadControl label="Graphic Page" onUpload={file => handleImageUpload({ target: { files: [file] } } as any, 'graphic')} onClear={() => setGraphicPageBgImg(null)} hasImage={!!graphicPageBgImg} />
                    <UploadControl label="Lineup Page" onUpload={file => handleImageUpload({ target: { files: [file] } } as any, 'lineup')} onClear={() => setLineupPageBgImg(null)} hasImage={!!lineupPageBgImg} />
                </div>
            </CollapsibleControlGroup>
            
            <div className="mt-auto pt-4 border-t border-gray-800">
                <label className="block text-[9px] font-black text-brand-muted mb-2 uppercase tracking-widest">Canvas Zoom: {zoom}%</label>
                <input type="range" min="10" max="50" value={zoom} onChange={e => setZoom(parseInt(e.target.value, 10))} className="w-full accent-brand-orange" />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen bg-brand-dark text-white font-sans overflow-hidden">
            <header className="flex-shrink-0 bg-brand-surface p-4 flex justify-between items-center border-b border-gray-700 z-30">
                <button onClick={() => handleNavigation('lineupEditor')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-black uppercase tracking-widest text-[10px]"><ArrowLeftIcon className="h-4 w-4" /><span>Back</span></button>
                <div className="flex bg-gray-700 rounded-full p-1">
                    <button onClick={() => setCurrentPage('graphic')} className={`py-1.5 px-4 rounded-full font-black text-[10px] uppercase tracking-widest transition-colors ${currentPage === 'graphic' ? 'bg-brand-orange text-white' : 'text-brand-muted'}`}>Graphic</button>
                    <button onClick={() => setCurrentPage('lineup')} className={`py-1.5 px-4 rounded-full font-black text-[10px] uppercase tracking-widest transition-colors ${currentPage === 'lineup' ? 'bg-brand-orange text-white' : 'text-brand-muted'}`}>Lineup</button>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setIsSidebarOpen(true)} className="md:hidden bg-gray-700 text-white p-2 rounded-full"><SettingsIcon className="h-4 w-4" /></button>
                    <button onClick={handleSaveFlyer} disabled={isSaving} className="bg-brand-orange text-white px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest disabled:opacity-50">{isSaving ? '...' : 'Save'}</button>
                    <button onClick={handleShare} disabled={isSharing} className="bg-gray-700 text-white p-2 rounded-full"><ShareIcon className="h-4 w-4" /></button>
                </div>
            </header>

            <div className="flex-grow flex relative min-h-0">
                {/* Desktop Sidebar */}
                <aside className="hidden md:flex w-80 bg-brand-surface flex-shrink-0 flex-col p-4 overflow-y-auto border-r border-gray-800 custom-scrollbar z-20">
                    {SidebarContent}
                </aside>

                {/* Mobile Drawer */}
                <div className={`fixed inset-0 bg-black/80 z-[100] transition-opacity duration-300 md:hidden ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsSidebarOpen(false)}>
                    <div className={`absolute inset-y-0 left-0 w-4/5 max-w-sm bg-brand-surface p-5 shadow-2xl transition-transform duration-300 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6 border-b border-gray-800 pb-4">
                            <h2 className="text-xl font-black uppercase tracking-tighter">Controls</h2>
                            <button onClick={() => setIsSidebarOpen(false)} className="p-2 bg-gray-800 rounded-full"><CloseIcon className="h-5 w-5" /></button>
                        </div>
                        <div className="overflow-y-auto h-[calc(100vh-120px)] custom-scrollbar pr-2">
                            {SidebarContent}
                        </div>
                    </div>
                </div>
                
                <main className="flex-grow flex items-center justify-center p-4 md:p-6 overflow-auto relative bg-brand-dark select-none z-10">
                    {isRendering && (
                        <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[2px] flex items-center justify-center z-50 pointer-events-none">
                            <div className="w-12 h-12 border-4 border-brand-orange border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                    <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'center' }} className="transition-transform duration-200">
                        <canvas 
                            ref={canvasRef} 
                            className="rounded-lg shadow-[0_0_50px_rgba(0,0,0,0.5)] bg-black cursor-crosshair touch-none" 
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                            onPointerLeave={handlePointerUp}
                        />
                    </div>
                </main>
            </div>
        </div>
    );
};

const CheckboxToggle: React.FC<{ label: string, checked: boolean, onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
    <label className="flex items-center space-x-2 cursor-pointer group">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="h-3 w-3 rounded bg-gray-800 border-gray-700 text-brand-orange focus:ring-brand-orange" />
        <span className="text-[10px] font-bold text-gray-300 group-hover:text-white uppercase tracking-widest transition-colors">{label}</span>
    </label>
);

const CollapsibleControlGroup: React.FC<{ label: string, children: React.ReactNode, defaultOpen?: boolean }> = ({ label, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border-t border-gray-700 first:border-t-0">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left py-3 group">
                <label className="text-xs font-bold text-white uppercase tracking-widest cursor-pointer group-hover:text-brand-orange transition-colors">{label}</label>
                <ChevronDownIcon className={`h-4 w-4 text-brand-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && <div className="pb-4 space-y-3">{children}</div>}
        </div>
    );
};

const UploadControl: React.FC<{ label: string, onUpload: (f: File) => void, onClear: () => void, hasImage: boolean }> = ({ label, onUpload, onClear, hasImage }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div className="flex items-center justify-between p-2 bg-gray-800/40 rounded-lg border border-gray-700">
            <span className="text-[9px] font-bold text-brand-muted uppercase tracking-widest">{label}</span>
            <div className="flex gap-2">
                {hasImage && <button onClick={onClear} className="p-1 text-red-400 hover:bg-red-400/10 rounded"><TrashIcon className="h-4 w-4" /></button>}
                <button onClick={() => inputRef.current?.click()} className="p-1 text-brand-orange hover:bg-brand-orange/10 rounded"><PhotoIcon className="h-4 w-4" /></button>
                <input type="file" ref={inputRef} className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])} />
            </div>
        </div>
    );
};

const Select: React.FC<{ label: string, value: string, onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void, options: string[] }> = ({ label, value, onChange, options }) => (
    <div><label className="block text-[9px] font-bold text-brand-muted mb-1 uppercase tracking-widest">{label}</label><select value={value} onChange={onChange} className="w-full bg-gray-800 text-white text-xs rounded-md p-2 outline-none border border-gray-700 focus:border-brand-orange font-bold">{options.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
);

export default FlyerPreviewPage;